import React from 'react';

// Design System Components
import Button from 'Components/DesignSystem/Button/Button';

// AlertTriangle icon for warning state
const AlertTriangleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 5.5v3M8 11h.01M6.86 2.573L1.21 12.29a1 1 0 00.86 1.51h11.06a1 1 0 00.86-1.51L8.34 2.573a1 1 0 00-1.72 0z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Search icon for button
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <circle cx="7" cy="7" r="5" strokeWidth="1.5" />
    <path strokeLinecap="round" strokeWidth="1.5" d="M11 11l3 3" />
  </svg>
);

interface ProductNotFoundIndicatorProps {
  onSearchProduct: () => void;
}

/**
 * ProductNotFoundIndicator Component
 *
 * Displays "Product Not Found" badge with "Search and Add Product" button
 * for unmatched products in the Unmatched tab.
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11674-17723
 *
 * Design specs:
 * - Badge: Grey/200 background (#dadbdd), Grey/900 text, 4px radius
 * - Badge includes AlertTriangle warning icon
 * - Button: Primary variant (Blue/900 background), Search icon
 * - Layout: Horizontal inline with gap
 */
const ProductNotFoundIndicator = ({ onSearchProduct }: ProductNotFoundIndicatorProps) => {
  return (
    // Badge and button stay on a single row. The parent colSpan={3} cell on
    // the Unmatched tab has roughly 35% of the table width (cols 2–4:
    // 15% + 11% + 9%), comfortably enough at standard viewports for both
    // pieces inline. Each child is `tw-flex-shrink-0 tw-whitespace-nowrap`
    // so their text never wraps; if the viewport is extreme-narrow the
    // table's own horizontal scroll kicks in rather than letting these two
    // stack, per the latest UX direction.
    // Compact layout: tighter gap, compact badge padding, shorter button
    // label ("Search & Add") so the badge + button together stay inside the
    // Product Catalog half (colSpan={3} in an 8-col table with the left
    // half spanning ~35% of total width) without crossing into the
    // Imported Products column. Full action description lives on the
    // button's native `title` tooltip for accessibility.
    <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">
      {/* Product Not Found Badge */}
      <span className="tw-inline-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-rounded tw-bg-tone-neutral-tint tw-text-ink tw-text-xs tw-font-medium tw-whitespace-nowrap">
        <span className="tw-text-ink">
          <AlertTriangleIcon />
        </span>
        Product Not Found
      </span>

      {/* Search & Add Button — primary variant, shortened label */}
      <div className="tw-flex-shrink-0 tw-whitespace-nowrap" title="Search and Add Product">
        <Button variant="primary" icon={<SearchIcon />} onClick={onSearchProduct}>
          Search &amp; Add
        </Button>
      </div>
    </div>
  );
};

export default ProductNotFoundIndicator;
